const name_length_threshold = 6;
const dependency_collision_threshold = 2;

export const pnpHeaders = [
  "sku",
  "related sku",
  "name",
  "price",
  "stock",
  "variation 1 name",
  "variation 1 value",
  "variation 2 name",
  "variation 2 value",
  "summary",
  "description",
  "images",
];

export const processInventory = (inventory) => {
  if (!inventory) {
    return null;
  }

  const validation = [];

  // Headers check
  let headersGood = true;
  for (let i = 0; i < pnpHeaders.length; ++i) {
    if (inventory.headers[i]?.toLowerCase() === "variation1 name") {
      headersGood = false;
      validation.push(
        validationMessage(
          `Headers check expected '${
            pnpHeaders[i]
          }' but found '${inventory.headers[i].toLowerCase()}'.` +
            "There should be a space between the 1 - this was a mistake early on in the inventory template.",
          "headers",
          null
        )
      );
    } else if (
      pnpHeaders[i].toLowerCase() !== inventory.headers[i]?.toLowerCase()
    ) {
      headersGood = false;
      validation.push(
        validationMessage(
          `Headers check expected '${
            pnpHeaders[i]
          }' but found '${inventory.headers[i]?.toLowerCase()}'.`,
          "headers",
          null
        )
      );
      break;
    }
  }

  if (!headersGood) {
    return validation;
  }

  const skuMap = {};
  const variationMap = {};
  const duplicateList = [];
  const dependedMap = {};

  // Preflight check: read SKUs and determine types
  inventory.rows.forEach((row, idx) => {
    const sku = row["sku"];
    const rsku = row["related sku"];
    const type = extractRowType(row);

    // Flag missing SKU
    if (!sku) {
      validation.push(
        validationMessage("is missing a SKU.", "sku", "sku", row)
      );
    }

    // Flag duplicate SKU
    if (skuMap[sku]) {
      duplicateList.push(idx);
      validation.push(
        validationMessage(
          `has a SKU ${sku} that is already used.`,
          "sku",
          "sku",
          row
        )
      );
    }

    // SKU with invalid characters
    if (sku && sku.indexOf("&") >= 0) {
      validation.push(
        validationMessage("must not contain &.", "sku", "sku", row)
      );
    }

    // Prep variation map
    if (sku && !skuMap[sku]) {
      skuMap[sku] = type;
      if (type === "variable") {
        variationMap[sku] = {};
      }
    }

    // Prep dependency map
    if (rsku) {
      dependedMap[rsku] = (dependedMap[rsku] || 0) + 1;
    }

    // TODO test for variable row setting stock when variations have stock
  });

  // Main check
  inventory.rows.forEach((row, idx) => {
    const sku = row["sku"];
    const rsku = row["related sku"];
    const name = row["name"];
    const price = row["price"];
    // eslint-disable-next-line no-unused-vars
    const stock = row["stock"];
    const v1name = row["variation 1 name"];
    const v1val = row["variation 1 value"];
    const v2name = row["variation 2 name"];
    const v2val = row["variation 2 value"];
    const summary = row["summary"];
    const description = row["description"];
    const images = row["images"];
    const type = extractRowType(row);
    let flag_maybe_simple = false;
    let flag_maybe_variation_main = false;

    if (type === "simple") {
      if (dependedMap[sku]) {
        validation.push(
          validationMessage(
            "should not have a price if it is a variation product main line.",
            "suspicious-variation-simple",
            "price",
            row
          )
        );
      }

      if (!name) {
        validation.push(
          validationMessage("is missing a name.", "product", "name", row)
        );
      }

      if (name && name.length <= name_length_threshold) {
        validation.push(
          validationMessage(
            "has a very short name. It is easier for shoppers to understand what a product is with longer and more descriptive names.",
            "product",
            "mame",
            row
          )
        );
      }

      if (
        !(summary && description) &&
        ((v1name && v1val) || (v2name && v2val))
      ) {
        validation.push(
          validationMessage(
            "should have a related SKU if it's a variable product.",
            "product",
            "related sku",
            row
          )
        );
      } else if (v1name || v1val || v2name || v2val) {
        validation.push(
          validationMessage(
            "should not have variation information if it is a simple product.",
            "variation-mainline-data",
            [
              "variation 1 name",
              "variation 1 value",
              "variation 2 name",
              "variation 2 value",
            ].filter((f) => row[f]?.length),
            row
          )
        );
      }

      if (!summary && !description) {
        validation.push(
          validationMessage(
            "is missing both a summary and a description.",
            "product",
            ["summary", "description"],
            row
          )
        );
      }

      if (!images) {
        validation.push(
          validationMessage("does not have an image.", "images", "images", row)
        );
      }
    }

    if (type === "variable") {
      if (v1name || v1val || v2name || v2val) {
        validation.push(
          validationMessage(
            "should not have variation information if it is a variation main line.",
            "variation-mainline-data",
            [
              "variation 1 name",
              "variation 1 value",
              "variation 2 name",
              "variation 2 value",
            ].filter((f) => row[f]?.length),
            row
          )
        );
      }

      if (!name) {
        validation.push(
          validationMessage("is missing a name.", "product", "name", row)
        );
      }

      if (name && name.length <= name_length_threshold) {
        validation.push(
          validationMessage(
            "has a very short name. It is easier for shoppers to understand what a product is with longer and more descriptive names.",
            "product",
            "name",
            row
          )
        );
      }

      if (!summary && !description) {
        validation.push(
          validationMessage(
            "is missing both a summary and a description.",
            "product",
            ["summary", "description"],
            row
          )
        );
      }

      if (!images) {
        validation.push(
          validationMessage("does not have an image.", "images", "images", row)
        );
      }
    }

    if (type === "variation") {
      // Check parent SKU
      if (!rsku) {
        validation.push(
          validationMessage(
            "related SKU must not be blank.",
            "related-sku",
            "related sku",
            row
          )
        );
      } else if (!skuMap[rsku] && skuMap[rsku.trim()]) {
        validation.push(
          validationMessage(
            `related SKU ${rsku} must match exactly. There seems to be extra spacing around the related SKU.`,
            "related-sku",
            "related sku",
            row
          )
        );
      } else if (!skuMap[rsku]) {
        validation.push(
          validationMessage(
            `related SKU ${rsku} does not exist. Verify that the SKUs match with uppercase and lowercase letters.`,
            "related-sku",
            "related sku",
            row
          )
        );
      } else if (
        skuMap[rsku] !== "variable" &&
        (dependedMap[rsku] ?? 0) < dependency_collision_threshold
      ) {
        validation.push(
          validationMessage(
            `related SKU ${rsku} is set for a product that is not a variation main line.`,
            "variation-invalid-parent",
            "related sku",
            row
          )
        );
      }

      if ((dependedMap[sku] ?? 0) >= dependency_collision_threshold) {
        flag_maybe_variation_main = true;
        validation.push(
          validationMessage(
            "should not have a Related SKU if it is a variation main line.",
            "suspicious-variation-main",
            "related sku",
            row
          )
        );
      }

      // If no variations and related SKU is not found, simple product?
      if (!skuMap[rsku] && !v1name && !v1val && !v2name && !v2val) {
        flag_maybe_simple = true;
        validation.push(
          validationMessage(
            "may have an accidental related SKU. Is this a variable product?",
            "suspicious-simple-variation",
            "related sku",
            row
          )
        );
      }
      // Check that a variation is used
      else if (!v1name && !v2name) {
        validation.push(
          validationMessage(
            "must fill out Variation 1 name or Variation 2 name or both if it is a variation sub line.",
            "variation-missing-data",
            ["variation 1 name", "variation 2 name"],
            row
          )
        );
      }

      if (v1name && !v1val) {
        validation.push(
          validationMessage(
            "must fill out Variation 1 value.",
            "variation-missing-data",
            "variation 1 value",
            row
          )
        );
      } else if (v1name && v1val.toString().indexOf(",") >= 0) {
        validation.push(
          validationMessage(
            "Variation 1 value cannot contain commas.",
            "product",
            "variation 1 value",
            row
          )
        );
      }

      if (v1val && !v1name) {
        validation.push(
          validationMessage(
            "must fill out Variation 1 name.",
            "variation-missing-data",
            "variation 1 name",
            row
          )
        );
      }

      if (v2name && !v2val) {
        validation.push(
          validationMessage(
            "must fill out Variation 2 value.",
            "variation-missing-data",
            "variation 2 value",
            row
          )
        );
      } else if (v2name && v2val.toString().indexOf(",") >= 0) {
        validation.push(
          validationMessage(
            "Variation 2 value cannot contain commas.",
            "product",
            "variation 2 value",
            row
          )
        );
      }

      if (v2val && !v2name) {
        validation.push(
          validationMessage(
            "must fill out Variation 2 name.",
            "variation-missing-data",
            "variation 2 name",
            row
          )
        );
      }
      if (rsku && variationMap[rsku]) {
        // Check variation 1 is consistent and entered
        if (v1name && !variationMap[rsku]["v1name"]) {
          variationMap[rsku]["v1name"] = v1name;
        } else if (
          variationMap[rsku]["v1name"] &&
          variationMap[rsku]["v1name"] !== v1name &&
          variationMap[rsku]["v1name"] === v1name?.trim()
        ) {
          validation.push(
            validationMessage(
              "must consistently use the same Variation 1 name. There seems to be extra space around the name.",
              "variation-inconsistent-name",
              "variation 1 name",
              row
            )
          );
        } else if (
          variationMap[rsku]["v1name"] &&
          variationMap[rsku]["v1name"] !== v1name
        ) {
          validation.push(
            validationMessage(
              `must consistently use the same Variation 1 name. ${
                !v2name
                  ? "Perhaps you meant to have two variation types for this product."
                  : ""
              }`,
              "variation-inconsistent-name",
              "variation 1 name",
              row
            )
          );
        }

        // Check variation 2 is consistent and entered
        if (v2name && !variationMap[rsku]["v2name"]) {
          variationMap[rsku]["v2name"] = v2name;
        } else if (
          variationMap[rsku]["v2name"] &&
          variationMap[rsku]["v2name"] !== v2name &&
          variationMap[rsku]["v2name"] === v2name?.trim()
        ) {
          validation.push(
            validationMessage(
              "must consistently use the same Variation 2 name. There seems to be extra space after the name.",
              "variation-inconsistent-name",
              "variation 2 name",
              row
            )
          );
        } else if (
          variationMap[rsku]["v2name"] &&
          variationMap[rsku]["v2name"] !== v2name
        ) {
          validation.push(
            validationMessage(
              `must consistently use the same Variation 2 name. ${
                !v1name
                  ? "Perhaps you mean to have two variation types for this product."
                  : ""
              }`,
              "variation-inconsistent-name",
              "variation 2 name",
              row
            )
          );
        }
      }

      // Check price
      if (!price && !flag_maybe_variation_main) {
        validation.push(
          validationMessage(
            "is a variation sub line and must have a price.",
            "product",
            "price",
            row
          )
        );
      }

      // Check no summary
      if (summary && !flag_maybe_simple && !flag_maybe_variation_main) {
        validation.push(
          validationMessage(
            "should not have a summary if it is a variation sub line.",
            "product",
            "summary",
            row
          )
        );
      }
    }

    // Process and test images
    const extTest = /\.(png|jpg|jpeg)$/i;
    const cleanImages =
      images &&
      images
        .toString()
        .trim()
        .replace(/[\r\n]+/g, ",")
        .split(/\s*,\s*/);

    if (cleanImages?.some((i) => !extTest.test(i))) {
      validation.push(
        validationMessage(
          "images must all have an extension specified (.jpg or .jpeg or .png) and only use commas for separating multiple images.",
          "images",
          "images",
          row
        )
      );
    }
    if (
      (cleanImages?.length ?? 0) > 1 &&
      type === "variation" &&
      !flag_maybe_simple
    ) {
      validation.push(
        validationMessage(
          "should have at most one image if it is a variation sub line.",
          "images",
          "images",
          row
        )
      );
    }
  });

  // Post check
  inventory.rows.forEach((row, idx) => {
    const sku = row["sku"];
    const rsku = row["related sku"];
    const type = extractRowType(row);

    if (type === "variable" && !Object.keys(variationMap[sku] || {}).length)
      validation.push(
        validationMessage(
          "is a variation main line, but no variations were detected. Be sure to use this row's SKU as the Related SKU for the related variation sub lines.",
          "variation-missing-data",
          null,
          row
        )
      );

    if (
      type === "variation" &&
      variationMap[rsku]?.["v1name"] !== row["variation 1 name"]
    )
      validation.push(
        validationMessage(
          "must consistently use the same Variation 1 name.",
          "variation-inconsistent-name",
          "variation 1 name",
          row
        )
      );

    if (
      type === "variation" &&
      variationMap[rsku]?.["v2name"] !== row["variation 2 name"]
    )
      validation.push(
        validationMessage(
          "must consistently use the same Variation 2 name.",
          "variation-inconsistent-name",
          "variation 2 name",
          row
        )
      );
  });

  return validation.length ? validation : false;
};

export const extractRowType = (row) => {
  if (!row["price"] && !row["related sku"]) {
    return "variable";
  } else if (row["related sku"]) {
    return "variation";
  } else {
    return "simple";
  }
};

export const extractSheetHeaders = (sheet) => {
  const headers = [];
  const header = /[A-Z]+1$/;

  for (const c of Object.keys(sheet)) {
    if (header.test(c)) {
      headers.push(sheet[c].v);
    }
  }

  // remove trailing personalization header
  if (headers[headers.length - 1].toLowerCase() === "personalization") {
    delete headers[headers.length - 1];
  }

  return headers;
};

const validationMessage = (message, context, heading, row = null) => [
  (row ? (row.name || row.sku || `Listing ${row._entryNum}`) + " " : "") +
    message,
  row ? row._entryNum : "headers",
  context,
  row ? heading : null,
  row ? row._rowNum : "headers",
];
